import { Button } from "components/DesignSystem/Button/Button";
import { ConditionalLink } from "components/conditionalLink";
import { SmallRightArrow } from "components/icons/SmallRightArrow";
import { CLICKED_FILING_CTA_ON_FILINGS_TIMELINE } from "constants/analyticsEvents";
import { TIMELINE_STATUS } from "constants/filingsTimeline";
import { categorizeFilingState } from "utils/categorizeFilingState";
import { useAnalytics } from "hooks/useAnalytics";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { openLink } from "utils/openLink";
import { useAddProductToCartMutation } from "store/apis/products";
import { TimelineFilingRow } from "store/apis/filingsTimeline";
import { getTimelineStatus } from "utils/filingsTimeline/getTimelineStatus";
import { useTimelineContext } from "./TimelineContext";

type TimelineDetailActionProps = {
  filing: TimelineFilingRow;
};

const FILINGS_ROUTE = "/tax/filings";

const VIEW_FILING_LABEL = "View filing";

const ADD_DETAILS_LABEL = "Add details";
const FORM_FILING_STATE = "FORM_FILING";
const GO_TO_CART_LABEL = "Go to cart";
const ADD_TO_CART_LABEL = "Add to cart";

const ADDED_TO_CART_MESSAGE = "Added to your cart";
const GENERIC_ERROR = "Could not add this filing to your cart";

export const TimelineDetailAction = ({ filing }: TimelineDetailActionProps) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const { successToast, alertToast } = useToast();
  const { onOpenCart } = useTimelineContext();
  const { trackEvent } = useAnalytics();

  const [addProductToCart, { isLoading }] = useAddProductToCartMutation();

  const trackCtaClick = (ctaLabel: string) =>
    trackEvent(CLICKED_FILING_CTA_ON_FILINGS_TIMELINE, {
      filing_name: filing.name,
      cta_label: ctaLabel,
    });

  const status = getTimelineStatus(filing);

  const needsDetails = filing.state_type
    ? categorizeFilingState(filing.state_type) === FORM_FILING_STATE
    : false;

  const isSuggested =
    status === TIMELINE_STATUS.UNPAID &&
    !filing.cart_status &&
    !filing.filing_id;

  const canAddToCart = Boolean(
    filing.product_id && filing.product_content_type_id
  );

  const addSuggestedFiling = async () => {
    if (!filing.product_id || !filing.product_content_type_id) return;

    try {
      await addProductToCart({
        groupId,
        entityId: filing.entity_id,
        payload: {
          product_id: filing.product_id,
          product_content_type_id: filing.product_content_type_id,
          entity_id: filing.entity_id,
          season: filing.tax_year,
          quantity: 1,
          deadline_date: filing.deadline,
          ...(filing.alarm_id ? { alarm_id: filing.alarm_id } : {}),
          ...(filing.state_id ? { state_id: filing.state_id } : {}),
        },
      }).unwrap();

      successToast({ title: ADDED_TO_CART_MESSAGE, message: filing.name });
      onOpenCart(filing.entity_id);
    } catch (error) {
      alertToast({
        message:
          (error as { data?: { message?: string } })?.data?.message ||
          GENERIC_ERROR,
      });
    }
  };

  const handleAddToCart = () => {
    trackCtaClick(ADD_TO_CART_LABEL);
    void addSuggestedFiling();
  };

  const openCart = () => {
    trackCtaClick(GO_TO_CART_LABEL);
    onOpenCart(filing.entity_id);
  };

  const openFilingInNewTab = () => {
    trackCtaClick(ADD_DETAILS_LABEL);
    openLink(`${FILINGS_ROUTE}/${filing.filing_id}`, "_blank");
  };

  const trackViewFilingClick = () => trackCtaClick(VIEW_FILING_LABEL);

  if (isSuggested) {
    return (
      <Button
        customType="primary"
        size="xsmall"
        onClick={handleAddToCart}
        isLoading={isLoading}
        disabled={isLoading || !canAddToCart}
      >
        <span className="t-flex t-items-center t-gap-1">
          {ADD_TO_CART_LABEL}
          <SmallRightArrow />
        </span>
      </Button>
    );
  }

  if (!filing.filing_id) {
    return (
      <Button customType="primary" size="xsmall" onClick={openCart}>
        <span className="t-flex t-items-center t-gap-1">
          {GO_TO_CART_LABEL}
          <SmallRightArrow />
        </span>
      </Button>
    );
  }

  if (needsDetails) {
    return (
      <Button customType="primary" size="xsmall" onClick={openFilingInNewTab}>
        <span className="t-flex t-items-center t-gap-1">
          {ADD_DETAILS_LABEL}
          <SmallRightArrow />
        </span>
      </Button>
    );
  }

  return (
    <ConditionalLink to={`${FILINGS_ROUTE}/${filing.filing_id}`}>
      <Button
        customType="secondary"
        size="xsmall"
        onClick={trackViewFilingClick}
      >
        {VIEW_FILING_LABEL}
      </Button>
    </ConditionalLink>
  );
};
